import React, { useState, useEffect, useCallback, useMemo } from 'react';
import WeatherIcon from 'components/other/WeatherIcon';
import GasConsumptionCost from 'components/other/GasConsumptionCost';
import Navigation from 'components/other/Navigation';
import { getGasConsumption } from 'services/gasConsumptionService';
import { listTariffs } from 'services/gasTariffService';
import { toLocalDate, fromLocalDate } from 'utils/localTime';
import { GasConsumptionData } from 'types';
import { getTotalCost } from 'services/totalCostService';

const last15 = (date: Date): Date => {
  const [year, monthIndex, day, hours, minutes] = toLocalDate(date);
  return fromLocalDate(
    year,
    monthIndex,
    day,
    hours,
    minutes - (minutes % 15),
    0
  );
};

export function HomeScreen() {
  const now = useMemo(() => new Date(), []);
  const [last24Consumption, setLast24Consumption] = useState<number>(-1);
  const [last24Cost, setLast24Cost] = useState<number>(-1);
  const [currentRateOfGas, setCurrentRateOfGas] = useState<number>(-1);

  const [year, monthIndex, day, hours, minutes] = toLocalDate(now);

  const periods: [string, string, Date][] = useMemo(
    () => [
      ['Annual', 'y', fromLocalDate(year, 0, 1, 0, 0, 0)],
      ['Monthly', 'm', fromLocalDate(year, monthIndex, 1, 0, 0, 0)],
      ['Daily', 'd', fromLocalDate(year, monthIndex, day, 0, 0, 0)],
      ['Hourly', 'h', fromLocalDate(year, monthIndex, day, hours, 0, 0)],
      ['Last 15 minutes', '', last15(now)],
    ],
    [year, monthIndex, day, hours, now]
  );

  const [data, setData] = useState(
    periods.map((p) => {
      const [period] = p;
      return {
        period,
        actual: 0,
        projected: 0,
        redHeight: '0rem',
        blueHeight: '0rem',
        cost: '$0',
      };
    })
  );

  const total = (a: GasConsumptionData[]) =>
    a.reduce((s: number, v: GasConsumptionData) => s + v.gas_consumption, 0);

  const getLast24Data = useCallback(async () => {
    const start = fromLocalDate(year, monthIndex, day - 1, hours, minutes, 0);
    const cost = (await getTotalCost(start, now)).total_cost;
    const { gas_consumption } = await getGasConsumption(start, now, 'h');
    setLast24Consumption(total(gas_consumption) / 24);
    setLast24Cost(cost);
  }, [now, day, hours, minutes, year, monthIndex]);

  const fetchChartData = useCallback(async () => {
    try {
      const [tariffs, consumptions] = await Promise.all([
        listTariffs(),
        Promise.all(
          periods.map(async ([period, group_by, start]) => {
            const cost = (await getTotalCost(start, now)).total_cost;
            const { gas_consumption, gas_consumption_projection } =
              await getGasConsumption(start, now, group_by);

            return [
              period,
              cost,
              [total(gas_consumption), total(gas_consumption_projection)],
            ];
          })
        ),
      ]);

      if (tariffs.length > 0) {
        const currentTariff = tariffs.find(
          tariff =>
            tariff.data.start_time_of_day <= hours &&
            hours < tariff.data.stop_time_of_day
        );

        setCurrentRateOfGas(currentTariff ? currentTariff.data.tariff_rate : 0);
      }

      setData(
        consumptions.map((c) => {
          const [period, cost, [actual, projected]] = c as [
            string,
            number,
            number[],
          ];
          const max = Math.max(actual, projected);
          const scale = max > 0 ? 100 / max : 0;
          const height = (value: number) => `${scale * value}%`;
          return {
            period: period,
            actual,
            projected,
            redHeight: height(actual),
            blueHeight: height(projected),
            cost: `$${cost.toFixed(2)}`,
          };
        })
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [periods, now, hours, setCurrentRateOfGas, setData]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  useEffect(() => {
    getLast24Data();
  }, [getLast24Data]);

  return (
    <div className="screen home-screen overflow-hidden bg-background text-white">
      <div className="mt-4 flex w-full justify-center">
        <div className="flex w-[90%] flex-col">
          {/* Weather and Time Display */}
          <div className="container mx-auto flex items-center justify-end space-x-2 px-20">
            <WeatherIcon /> {/* Custom component for weather icons */}
          </div>
          {/* Current Rate of Gas */}
          <div className="container mx-auto flex px-4 py-4">
            <div className="pr-10">
              <h1 className="pb-4 text-3xl uppercase">Current Tariff Rate</h1>
              <div className="flex items-baseline justify-center bg-white p-2 text-background">
                <span className="text-5xl">
                  {currentRateOfGas >= 0
                    ? currentRateOfGas.toFixed(3)
                    : 'Loading...'}
                </span>
                <span className="ml-2 align-text-bottom text-lg "> $/MCF</span>
              </div>
            </div>
            <div>
              <h1 className="pb-4 text-3xl uppercase">
                24 Hour Average Rate of Consumption and Total Cost
              </h1>
              <div className="flex gap-5">
                <div className="flex w-[45%] items-baseline justify-center bg-white p-2 text-background">
                  <span className="text-5xl">
                    {last24Consumption >= 0
                      ? last24Consumption.toFixed(2)
                      : 'Loading...'}
                  </span>
                  <span className="ml-2 align-text-bottom text-lg "> MCF/hr</span>
                </div>
                <div className="flex w-[45%] items-baseline justify-center bg-white p-2 text-background">
                  <span className="text-5xl">
                    {last24Cost >= 0 ? `$${last24Cost}` : 'Loading...'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <GasConsumptionCost data={data} />
          {/* Custom component for gas consumption cost */}
          {/* Legend */}
          <div className="mt-4 mt-5 flex justify-end">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 bg-blue-500"></div>
                <span>Projected gas consumption (MCF)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 bg-red-500"></div>
                <span className="text-white">Actual gas consumption (MCF)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex h-4 w-4 items-center justify-center text-3xl text-white">
                  $
                </div>
                <span className="text-white">Actual gas cost (USD)</span>
              </div>
            </div>
          </div>
          <Navigation />
        </div>
      </div>
    </div>
  );
}
